import type { PoljeStolpca, PreslikavaPotrebna } from '@koda/shared';
import { useState, type JSX } from 'react';

import { api } from '../lib/api';

interface Lastnosti {
  podjetje: string;
  /** Leto uvoza: preslikava se preverja proti viru, ki pri podjetju velja za to leto (5.2). */
  leto: number;
  /** Kaj je uvoz javil: stolpci datoteke, polja vira in dosedanja preslikava. */
  potrebna: PreslikavaPotrebna;
  /** Poklicano po shranjeni preslikavi; stran datoteko naloži še enkrat. */
  naShranjeno: () => void;
  naPreklic: () => void;
}

/**
 * Povezovanje stolpcev datoteke s podatki, ki jih uvoz potrebuje.
 *
 * Uvoz stolpce išče po imenu in pozna več različic imen. Kadar si je stranka
 * stolpce poimenovala po svoje, jih ne najde — tu admin pove, kateri stolpec
 * je kateri podatek. Preslikava se shrani **pri podjetju** in se pri vsakem
 * naslednjem uvozu uporabi samodejno, zato je to enkratno opravilo, ne
 * opravilo vsakega meseca (docs/ARHITEKTURA.md, 5.2).
 *
 * Datoteka je še vedno izbrana v obrazcu, zato jo stran po shranjevanju
 * pošlje znova — uporabniku je ni treba iskati.
 */
export function PreslikavaStolpcev({
  podjetje,
  leto,
  potrebna,
  naShranjeno,
  naPreklic,
}: Lastnosti): JSX.Element {
  // Zavihkov je pri Excelu lahko več; stolpci se povezujejo za tistega, ki ga
  // admin izbere. Pri CSV je en sam in brez imena.
  const [zavihek, setZavihek] = useState(potrebna.zavihki[0]?.ime ?? '');
  const izbran = potrebna.zavihki.find((z) => z.ime === zavihek) ?? potrebna.zavihki[0];

  // Kar je uvoz prepoznal sam, je že izpolnjeno; admin dopolni samo ostalo.
  const [izbira, setIzbira] = useState<Record<string, string>>({
    ...(izbran?.prepoznano ?? {}),
    ...potrebna.preslikava,
  });
  const [shranjujem, setShranjujem] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);

  const zamenjajZavihek = (ime: string): void => {
    setZavihek(ime);
    const novi = potrebna.zavihki.find((z) => z.ime === ime);
    setIzbira({ ...(novi?.prepoznano ?? {}), ...potrebna.preslikava });
  };

  /**
   * Polje je obvezno tudi, kadar se iz njega prepozna otvoritev.
   *
   * V shemi je lahko neobvezno (Pantheonov ključ temeljnice), brez njega pa
   * bi vse knjižbe pristale v svojem mesecu in začetna stanja bi bila
   * napačna — uvoz bi padel šele v ozadju, ko je preslikava že shranjena
   * (docs/ARHITEKTURA.md, 5.2).
   */
  const jeObvezno = (p: PoljeStolpca): boolean => p.obvezno || p.zaOtvoritev;

  const manjkajoObvezni = potrebna.polja
    .filter((p) => jeObvezno(p) && (izbira[p.polje] ?? '') === '')
    .map((p) => p.ime);

  const shrani = (): void => {
    setShranjujem(true);
    setNapaka(null);
    api
      .shraniPreslikavoStolpcev(podjetje, izbira, leto)
      .then(() => {
        naShranjeno();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Preslikave ni bilo mogoče shraniti.');
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  const vrstica = (polje: PoljeStolpca): JSX.Element => (
    <tr key={polje.polje} className={jeObvezno(polje) ? 'obvezno' : undefined}>
      <td>
        {polje.ime}
        {jeObvezno(polje) && <span className="oznaka">obvezno</span>}
        <span className="drobno namig-stolpca">
          {polje.zaOtvoritev
            ? 'Iz tega stolpca se prepozna otvoritev; brez njega bi bila začetna stanja napačna.'
            : polje.obvezno
              ? `Znana imena: ${polje.znanaImena.join(', ')}`
              : `Brez njega: ${polje.brezNjega ?? 'podatek ne bo zapisan'}`}
        </span>
      </td>
      <td>
        <select
          value={izbira[polje.polje] ?? ''}
          onChange={(d) => {
            setIzbira((prej) => ({ ...prej, [polje.polje]: d.target.value }));
          }}
        >
          <option value="">— ni v datoteki —</option>
          {(izbran?.stolpci ?? []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </td>
    </tr>
  );

  return (
    <section className="kartica preslikava">
      <h2>Poveži stolpce</h2>
      <p className="podnaslov">{potrebna.sporocilo}</p>
      <p className="namig">
        Preslikava se shrani pri tem podjetju in se pri naslednjem uvozu uporabi sama. Stolpec, ki
        ga v datoteki ni, pusti prazen.
      </p>

      {potrebna.zavihki.length > 1 && (
        <label className="drobno">
          Zavihek
          <select
            value={zavihek}
            onChange={(d) => {
              zamenjajZavihek(d.target.value);
            }}
          >
            {potrebna.zavihki.map((z) => (
              <option key={z.ime} value={z.ime}>
                {z.ime} ({z.stolpci.length} stolpcev)
              </option>
            ))}
          </select>
        </label>
      )}

      <table className="preslikava-tabela">
        <thead>
          <tr>
            <th>Podatek uvoza</th>
            <th>Stolpec v datoteki</th>
          </tr>
        </thead>
        <tbody>{potrebna.polja.map(vrstica)}</tbody>
      </table>

      {manjkajoObvezni.length > 0 && (
        <p className="opozorilo">
          Brez teh podatkov uvoz ne more teči: {manjkajoObvezni.join(', ')}.
        </p>
      )}
      {napaka !== null && <p className="napaka">{napaka}</p>}

      <div className="gumbi">
        <button
          type="button"
          onClick={shrani}
          disabled={shranjujem || manjkajoObvezni.length > 0}
        >
          {shranjujem ? 'Shranjujem …' : 'Shrani in naloži znova'}
        </button>
        <button type="button" className="tiho" onClick={naPreklic}>
          Prekliči
        </button>
      </div>
    </section>
  );
}
